import { useState, useMemo, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { InventarioRow, InventarioSortKey } from '@/types'
import { useIsAvpOnly, fmtArticulo } from '@/hooks/useIsAvpOnly'
import { useFiltrosPersistentes } from '@/hooks/useFiltrosPersistentes'
import { henoArticulo } from '@/utils/filtroArticulo'
import {
  catalogoDe, predicadoVariasGrafias, predicadoTextoAgregado,
} from '@/utils/filtroTexto'

// ── Constants ─────────────────────────────────────────────────────────────────
const PAGE_SIZE = 50

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useInventarioFilters(rows: InventarioRow[]) {
  const [, setSearchParams] = useSearchParams()
  // Los filtros de esta pantalla aguantan irse a otra y volver, durante la
  // sesión. `paramsIniciales` son los de la URL si se llegó con enlace, y si no
  // los que la sesión recuerda: hay que leerlos AQUÍ, en los inicializadores,
  // porque después no se vuelve a mirar la URL.
  const { paramsIniciales: params0, olvidar } = useFiltrosPersistentes('inventario')
  const isAvpOnly = useIsAvpOnly()
  const fmt = useCallback(
    (c: string | null | undefined) => fmtArticulo(c, isAvpOnly),
    [isAvpOnly],
  )

  // ── Filter state (synced to URL) ──────────────────────────────────────────
  // Términos combinados con OR dentro de cada campo, en la URL separados por «|».
  //
  // DOS buscadores donde antes había uno. El de antes metía código, descripción,
  // línea, familia y almacén en el mismo texto, así que dos términos sólo podían
  // combinarse con OR: pedir un artículo Y una descripción devolvía las dos
  // listas pegadas. Línea, familia y almacén NO se convierten en buscadores
  // porque ya tienen su propio desplegable ahí al lado; duplicar el control sería
  // peor que el problema.
  const [articulo, setArticuloState] = useState<string[]>(
    () => (params0.get('qa') ?? '').split('|').filter(Boolean),
  )
  const [descripcion, setDescripcionState] = useState<string[]>(
    () => (params0.get('qd') ?? '').split('|').filter(Boolean),
  )
  /** El buscador único de antes. Ningún control lo escribe ya, pero conserva su
   *  setter: su chip se tiene que poder quitar y «Limpiar todos», vaciarlo.
   *  Sobrevive para que los enlaces `?q=…` guardados sigan filtrando igual. */
  const [terminos, setTerminosState] = useState<string[]>(
    () => (params0.get('q') ?? '').split('|').filter(Boolean),
  )
  const [empresa,    setEmpresaState]    = useState(params0.get('empresa') ?? '')
  const [almacen,    setAlmacenState]    = useState(params0.get('almacen') ?? '')
  const [linea,      setLineaState]      = useState(params0.get('linea') ?? '')
  const [familia,    setFamiliaState]    = useState(params0.get('familia') ?? '')
  // tipo: '' = todos | 'final' = producto final ('P') | 'componente' = resto
  const [tipo,       setTipoState]       = useState(params0.get('tipo') ?? '')
  const [soloStock,  setSoloStockState]  = useState(params0.get('stock') === '1')
  const [sortKey,    setSortKeyState]    = useState<InventarioSortKey>(
    (params0.get('sort') as InventarioSortKey) ?? 'articulo',
  )
  const [sortDir,    setSortDirState]    = useState<'asc' | 'desc'>(
    (params0.get('dir') as 'asc' | 'desc') ?? 'asc',
  )
  const [page, setPage] = useState(1)

  // Setters that also sync URL
  function setTerminos(v: string[]) {
    setTerminosState(v); setPage(1)
    const unido = v.join('|')
    setSearchParams(prev => { unido ? prev.set('q', unido) : prev.delete('q'); return prev }, { replace: true })
  }
  function setArticulo(v: string[]) {
    setArticuloState(v); setPage(1)
    const unido = v.join('|')
    setSearchParams(prev => { unido ? prev.set('qa', unido) : prev.delete('qa'); return prev }, { replace: true })
  }
  function setDescripcion(v: string[]) {
    setDescripcionState(v); setPage(1)
    const unido = v.join('|')
    setSearchParams(prev => { unido ? prev.set('qd', unido) : prev.delete('qd'); return prev }, { replace: true })
  }
  function setEmpresa(v: string) {
    setEmpresaState(v); setPage(1)
    setSearchParams(prev => { v ? prev.set('empresa', v) : prev.delete('empresa'); return prev }, { replace: true })
  }
  function setAlmacen(v: string) {
    setAlmacenState(v); setPage(1)
    setSearchParams(prev => { v ? prev.set('almacen', v) : prev.delete('almacen'); return prev }, { replace: true })
  }
  function setLinea(v: string) {
    setLineaState(v); setPage(1)
    setSearchParams(prev => { v ? prev.set('linea', v) : prev.delete('linea'); return prev }, { replace: true })
  }
  function setFamilia(v: string) {
    setFamiliaState(v); setPage(1)
    setSearchParams(prev => { v ? prev.set('familia', v) : prev.delete('familia'); return prev }, { replace: true })
  }
  function setTipo(v: string) {
    setTipoState(v); setPage(1)
    setSearchParams(prev => { v ? prev.set('tipo', v) : prev.delete('tipo'); return prev }, { replace: true })
  }
  function setSoloStock(v: boolean) {
    setSoloStockState(v); setPage(1)
    setSearchParams(prev => { v ? prev.set('stock', '1') : prev.delete('stock'); return prev }, { replace: true })
  }
  function setSortKey(k: InventarioSortKey) {
    setSortKeyState(k)
    setSearchParams(prev => { prev.set('sort', k); return prev }, { replace: true })
  }
  function setSortDir(d: 'asc' | 'desc') {
    setSortDirState(d)
    setSearchParams(prev => { prev.set('dir', d); return prev }, { replace: true })
  }
  function clearAll() {
    setArticuloState([]); setDescripcionState([])
    setTerminosState([]); setEmpresaState(''); setAlmacenState('')
    setLineaState(''); setFamiliaState(''); setTipoState(''); setSoloStockState(false)
    setSortKeyState('articulo'); setSortDirState('asc'); setPage(1)
    // La URL vacía ya borra el recuerdo por sí sola; `olvidar` lo hace explícito
    // para que quien lea esto no tenga que deducirlo.
    olvidar()
    setSearchParams({}, { replace: true })
  }

  // Reset page when rows change
  useEffect(() => { setPage(1) }, [rows.length])

  // ── Filtered + sorted rows ────────────────────────────────────────────────
  // Catálogo de códigos completos, crudos y formateados: pedir un artículo
  // entero trae ese y no también el que lo lleva dentro.
  const catArticulo = useMemo(() => {
    const c = catalogoDe(rows, r => r.articulo)
    for (const r of rows) c.add(fmt(r.articulo).trim().toLowerCase())
    return c
  }, [rows, fmt])

  const filtered = useMemo(() => {
    // Un predicado por campo, y todos tienen que cumplirse.
    const casaArticulo = predicadoVariasGrafias(articulo, catArticulo)
    const casaDescripcion = predicadoTextoAgregado(descripcion)
    const lqs = terminos.map(x => x.toLowerCase())
    return rows.filter(r => {
      if (soloStock && r.cantidadFisicaDisponible <= 0) return false
      if (empresa && r.dataareaid !== empresa) return false
      if (almacen && r.almacen !== almacen) return false
      if (linea   && r.linea   !== linea)   return false
      if (familia && r.familia !== familia) return false
      if (tipo) {
        const esFinal = (r.tipoProducto ?? '').toUpperCase() === 'P'
        if (tipo === 'final'      && !esFinal) return false
        if (tipo === 'componente' &&  esFinal) return false
      }
      // El código entra CRUDO y FORMATEADO: con sólo el formateado, elegir una
      // opción del catálogo —cuyo valor es el crudo— no encontraba ni una fila.
      if (!casaArticulo([r.articulo, fmt(r.articulo)])) return false
      if (!casaDescripcion(r.descripcion)) return false
      // El buscador antiguo sigue barriendo el texto de siempre, para los
      // enlaces que aún lo traen. Es lo único que conserva el defecto, a
      // propósito: cambiarle el significado a un enlace repartido es peor.
      if (lqs.length > 0) {
        const haystack = henoArticulo(
          r.articulo, fmt, r.descripcion, r.linea, r.familia, r.almacen,
        )
        if (!lqs.some(q => haystack.includes(q))) return false
      }
      return true
    })
  }, [rows, articulo, descripcion, terminos, empresa, almacen, linea, familia, tipo, soloStock, fmt, catArticulo])

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') return dir * (av - bv)
      return dir * String(av).localeCompare(String(bv), 'es-ES')
    })
  }, [filtered, sortKey, sortDir])

  // ── Pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const safePage   = Math.min(page, totalPages)
  const paged      = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  // ── Active filter count ───────────────────────────────────────────────────
  const activeFilters = (articulo.length > 0 ? 1 : 0) + (descripcion.length > 0 ? 1 : 0) +
    (terminos.length > 0 ? 1 : 0) + (empresa ? 1 : 0) + (almacen ? 1 : 0) + (linea ? 1 : 0) + (familia ? 1 : 0) + (tipo ? 1 : 0) + (soloStock ? 1 : 0)

  return {
    // state
    articulo, descripcion, terminos, empresa, almacen, linea, familia, tipo, soloStock, sortKey, sortDir,
    // setters
    setArticulo, setDescripcion, setTerminos, setEmpresa, setAlmacen, setLinea, setFamilia, setTipo, setSoloStock, setSortKey, setSortDir, clearAll,
    // computed
    filtered, sorted, paged,
    page: safePage, setPage, totalPages, pageSize: PAGE_SIZE,
    activeFilters,
  }
}
